import clsx from 'clsx';
import styles from './Avatar.module.css';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  /** Full name or display text — used to compute initials. Tolerates
   *  null/undefined (e.g. a deal without an owner) — renders "?". */
  name?: string | null;
  /** Optional image url. If load fails, falls back to initials. */
  src?: string | null;
  size?: AvatarSize;
  className?: string;
  /** Surfaced as the alt attribute when image is shown. */
  alt?: string;
}

/** Compute "Priya Mehta" → "PM" / "Mark" → "MA". */
function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Stable hue from a string — for initials background tint. */
function hueFrom(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
}

/**
 * Round avatar. Renders image if `src` provided, otherwise initials on a
 * stable colour derived from `name`.
 */
export function Avatar({ name, src, size = 'md', className, alt }: AvatarProps) {
  const safeName = name ?? '';
  const initials = initialsFrom(safeName);
  const hue = hueFrom(safeName);

  return (
    <span
      className={clsx(styles.avatar, styles[`size_${size}`], className)}
      style={
        !src
          ? ({ ['--avatar-hue' as string]: String(hue) } as React.CSSProperties)
          : undefined
      }
      role="img"
      aria-label={alt ?? safeName ?? undefined}
    >
      {src ? (
        <img src={src} alt={alt ?? safeName} className={styles.image} />
      ) : (
        <span aria-hidden="true" className={styles.initials}>{initials}</span>
      )}
    </span>
  );
}
